import { useState, useEffect, useRef, useCallback, forwardRef } from "react";
import {
  Select,
  Button,
  Space,
  Typography,
  Divider,
  Card,
  InputNumber,
  Checkbox,
  Modal,
  Input,
  Form,
  message,
  Radio,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
//@ts-ignore
import { Scrollbars } from "@om-tlh/react-custom-scrollbars";
import "./ExamDesigner.less";
import { PageEditor } from "./PageEditor";
import { PaperDimension, PaperSize, Page, store } from "../Store";
import { useLiveQuery } from "dexie-react-hooks";
import { sendMessage } from "../Context";
import { isMobile } from "../Device";

// 内置纸张尺寸 (单位: mm)
const defaultPaperSizes: Record<string, PaperDimension> = {
  a4: { width: 210, height: 297, name: "A4" },
  letter: { width: 216, height: 279, name: "Letter" },
  legal: { width: 216, height: 356, name: "Legal" },
  a3: { width: 297, height: 420, name: "A3" },
  a5: { width: 148, height: 210, name: "A5" },
};

const { Text } = Typography;
const { Option } = Select;
const { Item: FormItem } = Form;
const win = global as any;

// 动态设置@page打印样式的函数
function setPrintPageStyle(
  paperSize: PaperDimension,
  orientation: "portrait" | "landscape",
  margin: number
) {
  // 1. 创建style标签
  const style = document.createElement("style");
  style.id = "dynamic-print-style"; // 给style标签加ID，方便后续修改/删除
  style.media = "print"; // 仅打印时生效（等价于@media print）

  // 2. 定义@page规则的CSS内容
  const pageCss = `
    @page {
      size:  ${
        paperSize.name?.startsWith("custom-")
          ? `${paperSize.width}mm ${paperSize.height}mm`
          : paperSize.name
      } ${orientation};
      margin: ${margin}mm;
    }
    /* 同时可添加打印样式优化 */
    body {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      font-size: 12pt;
    } 
  `;

  // 3. 将CSS内容插入style标签
  style.innerHTML = pageCss;

  // 4. 先移除已存在的同名style标签（避免重复）
  const oldStyle = document.getElementById("dynamic-print-style");
  if (oldStyle) {
    document.head.removeChild(oldStyle);
  }

  // 5. 将新style标签添加到head中
  document.head.appendChild(style);
}

const ExamDesigner = forwardRef((props: any, ref: any) => {
  // 状态管理
  const examPapger = useLiveQuery(() => store.getCurrentExamPapger());
  const [pages, setPages] = useState<Page[]>([]);
  const paperSize = examPapger?.paperSize || "a4";
  const orientation = examPapger?.orientation || "portrait";
  const [showGrid, setShowGrid] = useState<boolean>(false);
  const margin = examPapger?.margin || 15;
  const columns = examPapger?.columns || (isMobile ? 1 : 2);
  const [editingPageId, setEditingPageId] = useState<string>();
  const [customPaperSizes, setCustomPaperSizes] = useState<
    Record<string, PaperDimension>
  >({});
  const [isCustomSizeModalVisible, setIsCustomSizeModalVisible] =
    useState<boolean>(false);
  const [form] = Form.useForm();
  const exams = useLiveQuery(() =>
    store.exams
      .toArray()
      .then((data) =>
        data.sort((a, b) =>  new Date(a.createdAt! || '0').getTime() - new Date(b.createdAt! || '0').getTime())
      )
  );

  // 内容区域引用
  const contentRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // 获取所有可用纸张尺寸（内置 + 自定义）
  const getAllPaperSizes = useCallback((): Record<string, PaperDimension> => {
    return { ...defaultPaperSizes, ...customPaperSizes };
  }, [customPaperSizes]);

  // 添加新页面
  const addNewPage = () => {
    const newPage: Page = {
      id: crypto.randomUUID().replaceAll("-", ""),
      content: "",
      examId: examPapger?.id!,
    };

    store.addPage(newPage);
    setEditingPageId(newPage.id);
  };

  // 删除最后一页
  const removeLastPage = () => {
    if (!pages || pages.length <= 1) return;

    store.removePage(editingPageId!);
    setEditingPageId(
      pages.filter((it) => it.id !== editingPageId)[
        Math.min(
          pages.length - 2,
          pages.indexOf(pages.find((it) => it.id === editingPageId)!)
        )
      ].id
    );
  };

  // 更新页面内容
  const updatePageContent = useCallback((id: string, content: string) => {
    store.updatePage(id, { content });
  }, []);

  // 计算页面尺寸
  const getPageDimensions = useCallback((): PaperDimension => {
    const allSizes = getAllPaperSizes();
    const baseSize = allSizes[paperSize] || defaultPaperSizes.a4;

    return orientation === "portrait"
      ? { ...baseSize }
      : { width: baseSize.height, height: baseSize.width, name: baseSize.name };
  }, [getAllPaperSizes, paperSize, orientation]);

  // 打开自定义纸张尺寸弹窗
  const showCustomSizeModal = () => {
    form.resetFields();
    setIsCustomSizeModalVisible(true);
  };

  // 保存自定义纸张尺寸
  const saveCustomPaperSize = async () => {
    try {
      const values = await form.validateFields();
      const sizeId = `custom-${Date.now()}`;

      // 添加新的自定义尺寸
      setCustomPaperSizes((prev) => ({
        ...prev,
        [sizeId]: {
          width: Number(values.width),
          height: Number(values.height),
          name: values.name || `自定义尺寸 ${Object.keys(prev).length + 1}`,
        },
      }));

      // 切换到新创建的尺寸
      store.setPaperSize(sizeId as PaperSize);

      message.success("自定义纸张尺寸已保存");
      setIsCustomSizeModalVisible(false);
    } catch (error) {
      console.error("保存失败:", error);
    }
  };

  // 删除自定义纸张尺寸
  const deleteCustomPaperSize = (sizeId: PaperSize) => {
    // 如果当前正在使用该尺寸，切换到默认A4
    if (paperSize === sizeId) {
      store.setPaperSize("a4");
    }

    // 从状态中删除
    setCustomPaperSizes((prev) => {
      const newSizes = { ...prev };
      delete newSizes[sizeId];
      return newSizes;
    });

    message.success("自定义纸张尺寸已删除");
  };

  useEffect(() => {
    setPrintPageStyle(getPageDimensions(), orientation, margin);
  }, [orientation, margin, getPageDimensions]);

  useEffect(() => {
    win.pageId = editingPageId;
  }, [editingPageId]);

  useEffect(() => {
    setPages(examPapger?.pages || []);
  }, [examPapger?.pages]);

  useEffect(() => {
    setEditingPageId(examPapger?.pages?.[0]?.id || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examPapger?.id]);

  // 更新网格布局
  useEffect(() => {
    const container = document.querySelector(".pagesContainer");
    if (container) {
      (
        container as HTMLElement
      ).style.gridTemplateColumns = `repeat(${columns}, auto)`;
    }
  }, [columns]);

  // 渲染单个页面
  const renderPage = (page: Page, index: number) => {
    const { width, height } = getPageDimensions();
    const isActive = editingPageId === page.id;

    return (
      <Card
        key={page.id}
        className={`pageCard ${isActive ? "active" : ""}`}
        style={{
          width: `${width}mm`,
          height: `${height}mm`,
        }}
        styles={{
          body: {
            padding: 0,
            height: "100%",
            position: "relative",
          },
        }}
        onClick={(e) => {
          setEditingPageId(page.id);
          e.stopPropagation();
        }}
      >
        <div
          ref={(el) => (contentRefs.current[page.id] = el)}
          className={`pageContent ${showGrid ? "gridBg" : ""}`}
          style={{
            padding: `${margin}mm`,
          }}
        >
          <PageEditor
            id={page.id}
            value={page.content}
            onChange={(txt) => {
              updatePageContent(page.id, txt);
            }}
            onPageSplit={(splitInfo) => {
              let nextpage = pages![index + 1];
              if (!nextpage) {
                nextpage = {
                  id: crypto.randomUUID().replaceAll("-", ""),
                  content: splitInfo.text,
                  examId: examPapger?.id!,
                  index: index + 1,
                };
                setPages(
                  pages
                    .slice(0, index + 1)
                    .concat([nextpage])
                    .concat(pages.slice(index + 1))
                );
                store.addPage(nextpage, index + 1);
                setEditingPageId(nextpage.id);
                process.env.NODE_ENV === "development" &&
                  console.log("添加新页面", nextpage.id);
                win.pageId = nextpage.id;
              } else {
                sendMessage(
                  nextpage.id,
                  splitInfo.delta,
                  "before",
                  splitInfo.focus
                );
              }
              setEditingPageId(nextpage.id);
            }}
          ></PageEditor>
        </div>

        {/* 虚线边框 */}
        <div
          className="pageBorder"
          style={{
            padding: `${margin}mm`,
          }}
        />

        {/* 页码指示器 */}
        <div className="pageNumber">第 {index + 1} 页</div>
      </Card>
    );
  };

  // 渲染纸张尺寸选项
  const renderPaperSizeOptions = () => {
    const options = [];

    // 内置尺寸
    Object.entries(defaultPaperSizes).forEach(([key, size]) => {
      options.push(
        <Option key={key} value={key as PaperSize}>
          {key.toUpperCase()} ({size.width}×{size.height}mm)
        </Option>
      );
    });

    // 自定义尺寸分隔线
    if (Object.keys(customPaperSizes).length > 0) {
      options.push(
        <Divider key="custom-divider" style={{ margin: "5px 0" }}>
          自定义尺寸
        </Divider>
      );
    }

    // 自定义尺寸
    Object.entries(customPaperSizes).forEach(([key, size]) => {
      options.push(
        <Option
          key={key}
          value={key as PaperSize}
          className="paperSizeSelect-customTag"
        >
          <Space size="small">
            <span>
              {size.name} ({size.width}×{size.height}mm)
            </span>
            <DeleteOutlined
              size={12}
              onClick={(e) => {
                e.stopPropagation();
                deleteCustomPaperSize(key as PaperSize);
              }}
            />
          </Space>
        </Option>
      );
    });

    return options;
  };

  const gtView = paperSize
    ? columns *
        (
          defaultPaperSizes[paperSize] ||
          customPaperSizes[paperSize].width
        ).width *
        3.7795 >
      win.innerWidth
    : false;

  const scale = Math.min(
    1,
    paperSize && gtView
      ? (win.innerWidth - 50) /
          ((
            defaultPaperSizes[paperSize] ||
            customPaperSizes[paperSize]
          ).width *
            3.7795)
      : 1
  );

  return (
    <div className="ep-page-designer">
      <div className="toolBar">
        <div className="toolGroup">
          <Text>试卷:</Text>
          <Space size="small">
            <Select
              value={examPapger?.id}
              style={{ width: 140 }}
              onChange={(value) => store.setOption("editingExamId", value)}
              options={exams?.map((it) => ({
                label: it.name || "试卷" + it.createdAt?.toLocaleDateString(),
                value: it.id,
              }))}
              popupRender={(menu) => (
                <>
                  {menu}
                  <Divider style={{ margin: "2px 0" }} />
                  <Button
                    type="text"
                    style={{ width: "100%" }}
                    icon={<PlusOutlined />}
                    onClick={async () => {
                      const { pageId } = await store.addExam({
                        name: "试卷" + new Date().toLocaleDateString(),
                      });
                      setEditingPageId(pageId as string);
                    }}
                  >
                    新增试卷
                  </Button>
                </>
              )}
            ></Select>
          </Space>
        </div>

        <Divider type="vertical" className="divider" />

        {/* 纸张尺寸选择 */}
        <div className="toolGroup">
          <Text>纸张尺寸:</Text>
          <Space size="small">
            <Select
              value={paperSize}
              style={{ width: 200 }}
              onChange={(value: PaperSize) => store.setPaperSize(value)}
              className="paperSizeSelect"
              popupRender={(menu) => {
                return (
                  <>
                    {menu}
                    <Divider style={{ margin: "2px 0" }} />
                    <Button
                      icon={<PlusOutlined />}
                      style={{ width: "100%" }}
                      type="text"
                      onClick={showCustomSizeModal}
                    ></Button>
                  </>
                );
              }}
            >
              {renderPaperSizeOptions()}
            </Select>
          </Space>
        </div>

        {/* 方向选择 */}
        <div className="toolGroup">
          <Text>方向:</Text>
          <Space size="small">
            <Radio.Group
              value={orientation}
              onChange={(e) => store.setOrientation(e.target.value)}
            >
              <Radio.Button value="portrait">纵向</Radio.Button>
              <Radio.Button value="landscape">横向</Radio.Button>
            </Radio.Group>
          </Space>
        </div>

        {/* 网格显示 */}
        <Checkbox
          checked={showGrid}
          onChange={(e) => setShowGrid(e.target.checked)}
        >
          显示网格
        </Checkbox>

        {/* 页边距设置 */}
        <div className="toolGroup">
          <Text>页边距:</Text>
          <InputNumber
            value={margin}
            min={0}
            max={50}
            step={5}
            onChange={(value) => store.setMargin(value || 0)}
            style={{ width: 100 }}
            formatter={(value) => `${value}mm`}
            parser={(value) => parseInt(value?.replace("mm", "")!, 10)}
          />
        </div>

        {/* 列数设置 */}
        <div className="toolGroup">
          <Text>每行页数:</Text>
          <Select
            value={columns}
            style={{ width: 100 }}
            onChange={(value: number) => store.setColumns(value)}
          >
            <Option value={1}>1列</Option>
            <Option value={2}>2列</Option>
            <Option value={3}>3列</Option>
            <Option value={4}>4列</Option>
          </Select>
        </div>

        <Divider type="vertical" className="divider" />

        {/* 页面管理 */}
        <Space size="small">
          <Button type="default" icon={<PlusOutlined />} onClick={addNewPage}>
            添加页面
          </Button>
          <Button
            type="default"
            icon={<DeleteOutlined />}
            onClick={removeLastPage}
            disabled={!pages || pages.length <= 1}
          >
            删除页面
          </Button>
        </Space>

        {/* 页码信息 */}
        <Text type="secondary" className="pageCount">
          共 {pages?.length} 页
        </Text>
      </div>

      <div className="content" ref={ref}>
        <Scrollbars autoHide className="pagesScrollbar">
          <div
            className="pagesHolder"
            style={{
              justifyContent: gtView ? "left" : "center",
              transform: "scale(" + scale + ")",
            }}
          >
            <div className={"pagesContainer p" + pages?.length}>
              {pages?.map((page, index) => renderPage(page, index))}
            </div>
          </div>
        </Scrollbars>
      </div>

      {/* 自定义纸张尺寸弹窗 */}
      <Modal
        title="添加自定义纸张尺寸"
        open={isCustomSizeModalVisible}
        onCancel={() => setIsCustomSizeModalVisible(false)}
        onOk={saveCustomPaperSize}
        className="customSizeModal"
      >
        <Form form={form} layout="vertical">
          <FormItem
            name="name"
            label="尺寸名称"
            rules={[{ required: true, message: "请输入尺寸名称" }]}
            className="formItem"
          >
            <Input placeholder="例如：自定义A6" />
          </FormItem>
          <FormItem
            name="width"
            label="宽度 (mm)"
            rules={[
              { required: true, message: "请输入宽度" },
              { type: "number", min: 50, max: 1000 },
            ]}
            className="formItem"
          >
            <InputNumber
              placeholder="宽度"
              style={{ width: "100%" }}
              step={1}
            />
          </FormItem>
          <FormItem
            name="height"
            label="高度 (mm)"
            rules={[
              { required: true, message: "请输入高度" },
              { type: "number", min: 50, max: 1000 },
            ]}
            className="formItem"
          >
            <InputNumber
              placeholder="高度"
              style={{ width: "100%" }}
              step={1}
            />
          </FormItem>
        </Form>
      </Modal>
    </div>
  );
});

export default ExamDesigner;
